import { databases } from '../lib/appwrite';
import { ID, Query } from 'appwrite';

// ============================================
// ORDER SERVICE
// ============================================

const DATABASE_ID = process.env.REACT_APP_APPWRITE_DATABASE_ID;
const ORDER_COLLECTION_ID = process.env.REACT_APP_APPWRITE_ORDER_COLLECTION_ID;
const ORDERITEM_COLLECTION_ID = process.env.REACT_APP_APPWRITE_ORDERITEM_COLLECTION_ID;

/**
 * Create a new order
 * @param {Object} orderData - The order data
 * @param {string} orderData.customerId - Customer's user ID
 * @param {number} orderData.totalAmount - Total amount paid
 * @param {number} orderData.pointsRedeemed - Points redeemed for discount
 * @param {string} orderData.pickupId - Pickup point ID
 * @param {string} orderData.status - Order status (default: 'pending')
 * @returns {Promise<Object>} - Created order document
 */
export const createOrder = async (orderData) => {
  try {
    console.log('Creating order:', orderData);

    if (!DATABASE_ID || !ORDER_COLLECTION_ID) {
      throw new Error('Database configuration is missing');
    }

    const order = await databases.createDocument(
      DATABASE_ID,
      ORDER_COLLECTION_ID,
      ID.unique(),
      {
        customer_id: orderData.customerId,
        total_amount: orderData.totalAmount,
        points_redeemed: orderData.pointsRedeemed || 0,
        order_date: new Date().toISOString(),
        pickup_id: orderData.pickupId || null
      }
    );

    console.log('Order created:', order);
    return order;
  } catch (error) {
    console.error('Error creating order:', error);
    throw error;
  }
};

/**
 * Create order items for an order
 * @param {string} orderId - The order ID
 * @param {Array} items - Array of items to add
 * @returns {Promise<Array>} - Created order item documents
 */
export const createOrderItems = async (orderId, items) => {
  try {
    console.log('Creating order items for order:', orderId, items);

    if (!DATABASE_ID || !ORDERITEM_COLLECTION_ID) {
      throw new Error('Database configuration is missing');
    }

    const orderItems = await Promise.all(
      items.map(async (item) => {
        const orderItem = await databases.createDocument(
          DATABASE_ID,
          ORDERITEM_COLLECTION_ID,
          ID.unique(),
          {
            order_id: orderId,
            food_id: item.foodId,
            quantity: item.quantity,
            price_at_purchase: item.pricePerUnit,
            status: 'pending'
          }
        );
        return orderItem;
      })
    );

    console.log('Order items created:', orderItems);
    return orderItems;
  } catch (error) {
    console.error('Error creating order items:', error);
    throw error;
  }
};

/**
 * Create a complete order with items
 * @param {Object} orderData - The order data
 * @param {Array} items - The items to include in the order
 * @returns {Promise<Object>} - The created order with items
 */
export const createCompleteOrder = async (orderData, items) => {
  try {
    // Create the order first
    const order = await createOrder(orderData);

    // Then create the order items
    const orderItems = await createOrderItems(order.$id, items);

    return {
      order,
      orderItems
    };
  } catch (error) {
    console.error('Error creating complete order:', error);
    throw error;
  }
};

/**
 * Get all orders for a customer
 * @param {string} customerId - Customer's user ID
 * @returns {Promise<Array>} - Array of orders
 */
export const getOrdersByCustomerId = async (customerId) => {
  try {
    if (!customerId) {
      throw new Error('Customer ID is required');
    }

    const response = await databases.listDocuments(
      DATABASE_ID,
      ORDER_COLLECTION_ID,
      [
        Query.equal('customer_id', customerId),
        Query.orderDesc('order_date')
      ]
    );

    console.log('Orders fetched:', response.documents);
    return response.documents;
  } catch (error) {
    console.error('Error fetching orders:', error);
    throw error;
  }
};

/**
 * Get order items for a specific order
 * @param {string} orderId - The order ID
 * @returns {Promise<Array>} - Array of order items
 */
export const getOrderItems = async (orderId) => {
  try {
    if (!orderId) {
      throw new Error('Order ID is required');
    }

    const response = await databases.listDocuments(
      DATABASE_ID,
      ORDERITEM_COLLECTION_ID,
      [Query.equal('order_id', orderId)]
    );

    return response.documents;
  } catch (error) {
    console.error('Error fetching order items:', error);
    throw error;
  }
};

/**
 * Get a single order by ID
 * @param {string} orderId - The order ID
 * @returns {Promise<Object>} - The order document
 */
export const getOrderById = async (orderId) => {
  try {
    const order = await databases.getDocument(
      DATABASE_ID,
      ORDER_COLLECTION_ID,
      orderId
    );

    return order;
  } catch (error) {
    console.error('Error fetching order:', error);
    throw error;
  }
};

/**
 * Update order status
 * @param {string} orderId - The order ID
 * @param {string} status - New status (pending, preparing, ready_for_pickup, completed)
 * @returns {Promise<Object>} - Updated order document
 */
export const updateOrderStatus = async (orderId, status) => {
  try {
    const validStatuses = ['pending', 'preparing', 'ready_for_pickup', 'completed'];
    if (!validStatuses.includes(status)) {
      throw new Error(`Invalid status. Must be one of: ${validStatuses.join(', ')}`);
    }

    const updatedOrder = await databases.updateDocument(
      DATABASE_ID,
      ORDER_COLLECTION_ID,
      orderId,
      { status }
    );

    console.log('Order status updated:', updatedOrder);
    return updatedOrder;
  } catch (error) {
    console.error('Error updating order status:', error);
    throw error;
  }
};

/**
 * Get orders by status
 * @param {string} customerId - Customer's user ID
 * @param {string} status - Order status to filter by
 * @returns {Promise<Array>} - Array of orders
 */
export const getOrdersByStatus = async (customerId, status) => {
  try {
    const response = await databases.listDocuments(
      DATABASE_ID,
      ORDER_COLLECTION_ID,
      [
        Query.equal('customer_id', customerId),
        Query.equal('status', status),
        Query.orderDesc('order_date')
      ]
    );

    return response.documents;
  } catch (error) {
    console.error('Error fetching orders by status:', error);
    throw error;
  }
};

/**
 * Get active orders (pending, preparing, ready_for_pickup)
 * @param {string} customerId - Customer's user ID
 * @returns {Promise<Array>} - Array of active orders
 */
export const getActiveOrders = async (customerId) => {
  try {
    const response = await databases.listDocuments(
      DATABASE_ID,
      ORDER_COLLECTION_ID,
      [
        Query.equal('customer_id', customerId),
        Query.notEqual('status', 'completed'),
        Query.orderDesc('order_date')
      ]
    );

    return response.documents;
  } catch (error) {
    console.error('Error fetching active orders:', error);
    throw error;
  }
};

/**
 * Get completed/past orders
 * @param {string} customerId - Customer's user ID
 * @returns {Promise<Array>} - Array of completed orders
 */
export const getCompletedOrders = async (customerId) => {
  try {
    const response = await databases.listDocuments(
      DATABASE_ID,
      ORDER_COLLECTION_ID,
      [
        Query.equal('customer_id', customerId),
        Query.equal('status', 'completed'),
        Query.orderDesc('order_date')
      ]
    );

    return response.documents;
  } catch (error) {
    console.error('Error fetching completed orders:', error);
    throw error;
  }
};

/**
 * Get all order items (for vendor views)
 * @returns {Promise<Array>} - Array of all order items
 */
export const getAllOrderItems = async () => {
  try {
    const response = await databases.listDocuments(
      DATABASE_ID,
      ORDERITEM_COLLECTION_ID,
      [Query.orderDesc('$createdAt')]
    );

    return response.documents;
  } catch (error) {
    console.error('Error fetching all order items:', error);
    throw error;
  }
};

/**
 * Get order items by food IDs (for vendor to see orders of their items)
 * @param {Array<string>} foodIds - Array of food item IDs
 * @returns {Promise<Array>} - Array of order items
 */
export const getOrderItemsByFoodIds = async (foodIds) => {
  try {
    if (!foodIds || foodIds.length === 0) {
      return [];
    }

    // Appwrite Query.equal with array does OR matching
    const response = await databases.listDocuments(
      DATABASE_ID,
      ORDERITEM_COLLECTION_ID,
      [Query.equal('food_id', foodIds), Query.orderDesc('$createdAt')]
    );

    return response.documents;
  } catch (error) {
    console.error('Error fetching order items by food IDs:', error);
    throw error;
  }
};

/**
 * Get a single order item by ID
 * @param {string} orderItemId - The order item ID
 * @returns {Promise<Object>} - The order item document
 */
export const getOrderItemById = async (orderItemId) => {
  try {
    const orderItem = await databases.getDocument(
      DATABASE_ID,
      ORDERITEM_COLLECTION_ID,
      orderItemId
    );

    return orderItem;
  } catch (error) {
    console.error('Error fetching order item:', error);
    throw error;
  }
};

/**
 * Update order item status
 * @param {string} orderItemId - The order item ID
 * @param {string} status - New status (pending, preparing, ready_for_pickup, completed, not_completed)
 * @returns {Promise<Object>} - Updated order item document
 */
export const updateOrderItemStatus = async (orderItemId, status) => {
  try {
    const validStatuses = ['pending', 'preparing', 'ready_for_pickup', 'completed', 'not_completed'];
    if (!validStatuses.includes(status)) {
      throw new Error(`Invalid status. Must be one of: ${validStatuses.join(', ')}`);
    }

    const updatedOrderItem = await databases.updateDocument(
      DATABASE_ID,
      ORDERITEM_COLLECTION_ID,
      orderItemId,
      { status }
    );

    console.log('Order item status updated:', updatedOrderItem);
    return updatedOrderItem;
  } catch (error) {
    console.error('Error updating order item status:', error);
    throw error;
  }
};
